import { LitElement, html } from 'lit';
import {
    downloadQuoteDocument,
    downloadInvoiceDocument,
    DocumentNotReadyError,
    ContactNotAuthorizedError,
} from '../../services/apis/quote-api-service.js';
import { NotificationBus } from '../../services/notification-bus.js';
import '../icons/download-icon.js';

/**
 * @component document-download-card
 * @description Shared document download card — fetches the PDF with auth headers and triggers browser save.
 * Used by both quote-pending-payment (invoice) and quote-confirmed (quote document) pages.
 *
 * @property {String} quoteReference - Quote reference number (display only)
 * @property {String} quoteId - Quote ID for the download API call
 * @property {String} custRef - PO reference for display (optional)
 * @property {String} cardTitle - Card heading (e.g. "Invoice", "Quote Document")
 * @property {String} buttonText - Download button label
 * @property {String} documentType - "quote" (default) or "invoice" — determines which API endpoint to hit
 */
class DocumentDownloadCard extends LitElement {
    static properties = {
        quoteReference: { type: String },
        quoteId: { type: String },
        custRef: { type: String },
        cardTitle: { type: String },
        cardDescription: { type: String },
        cardNote: { type: String },
        buttonText: { type: String },
        documentType: { type: String },
        _downloading: { type: Boolean, state: true },
    };

    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.quoteReference = '';
        this.quoteId = '';
        this.custRef = '';
        this.cardTitle = 'Document';
        this.cardDescription = '';
        this.cardNote = '';
        this.buttonText = 'Download Document';
        this.documentType = 'quote';
        this._downloading = false;

        // Client-side countdown for "not ready" state.
        // The API always returns the same Retry-After value regardless of elapsed
        // time, so we track the first 409 timestamp locally and subtract elapsed
        // time on subsequent attempts. This is best-effort -- a page reload resets
        // the countdown, which is acceptable since the document may be ready by then.
        this._notReadyTimestamp = null; // epoch ms of the first 409
        this._notReadyMinutes = null; // original minutes from the API
    }

    /**
     * Adjust the "try again in X minutes" message based on elapsed time since
     * the first 409. The API always returns the same Retry-After regardless of
     * how long ago the document was requested, so we count down locally.
     * Resets on page reload -- good enough for a single session.
     */
    _getAdjustedMinutes(apiMinutes) {
        if (!apiMinutes) return null;

        const now = Date.now();

        if (!this._notReadyTimestamp) {
            // First 409 -- start the countdown
            this._notReadyTimestamp = now;
            this._notReadyMinutes = apiMinutes;
            return apiMinutes;
        }

        const elapsedMinutes = (now - this._notReadyTimestamp) / 60000;
        const remaining = Math.ceil(this._notReadyMinutes - elapsedMinutes);

        if (remaining <= 0) {
            return null;
        }

        return remaining;
    }

    async _handleDownload() {
        if (this._downloading || !this.quoteId) return;

        this._downloading = true;
        try {
            const downloadFn =
                this.documentType === 'invoice' ? downloadInvoiceDocument : downloadQuoteDocument;
            await downloadFn(this.quoteId);
        } catch (err) {
            if (err instanceof ContactNotAuthorizedError) {
                NotificationBus.show(
                    'Your account does not have access to download this document. Please check you are signed in with the correct email address.',
                    'warning',
                    8000,
                    'DocumentDownload'
                );
            } else if (err instanceof DocumentNotReadyError) {
                const minutes = this._getAdjustedMinutes(err.retryAfterMinutes);
                const timeMsg = minutes
                    ? `Please try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`
                    : 'Please try again shortly.';
                NotificationBus.show(
                    `Your document is still being generated. ${timeMsg}`,
                    'warning',
                    8000,
                    'DocumentDownload'
                );
            } else {
                console.error('Error downloading document:', err);
                NotificationBus.show(
                    'Failed to download document. Please try again.',
                    'error',
                    5000,
                    'DocumentDownload'
                );
            }
        } finally {
            this._downloading = false;
        }
    }

    render() {
        return html`
            <div class="card bg-base-200">
                <div class="card-body">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h3 class="card-title text-xl">${this.cardTitle}</h3>
                            <p class="text-sm text-gray-600 mt-1">
                                ${this.cardDescription
                                    ? this.cardDescription
                                    : html`Download your ${this.cardTitle.toLowerCase()} for quote
                                          <strong>${this.quoteReference}</strong> for your records.`}
                            </p>
                            ${this.cardNote
                                ? html`<p class="text-sm text-gray-600 mt-1">${this.cardNote}</p>`
                                : ''}
                            ${this.custRef
                                ? html`<p class="text-sm text-gray-600 mt-1">
                                      PO Reference:
                                      <strong>${this.custRef}</strong>
                                  </p>`
                                : ''}
                        </div>
                        <button
                            @click="${this._handleDownload}"
                            class="btn btn-primary btn-outline shrink-0"
                            ?disabled="${this._downloading}"
                        >
                            ${this._downloading
                                ? html`<span
                                      class="loading loading-spinner loading-sm mr-2"
                                  ></span>`
                                : html`<download-icon size="5" class="mr-2"></download-icon>`}
                            ${this._downloading ? 'Downloading...' : this.buttonText}
                        </button>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('document-download-card', DocumentDownloadCard);
